// Shared constants for DBOS Dashboard components

import type { DbosWorkflowStatus } from '../types/DbosTypes';

export const DBOS_STATUS_COLORS: Record<DbosWorkflowStatus, string> = {
    SUCCESS: '#28a745',
    PENDING: '#ffc107',
    ERROR: '#dc3545',
    MAX_RECOVERY_ATTEMPTS_EXCEEDED: '#e65100',
    CANCELLED: '#6c757d',
    ENQUEUED: '#1565c0',
};

export const DBOS_STATUS_CHART_COLORS: string[] = [
    '#ffc107', // PENDING
    '#28a745', // SUCCESS
    '#dc3545', // ERROR
    '#e65100', // MAX_RECOVERY
    '#6c757d', // CANCELLED
    '#1565c0', // ENQUEUED
];

/**
 * Format milliseconds to a human-readable duration string.
 * Examples: "0.8s", "12.5s", "1m 35s", "2h 15m"
 */
export const formatDurationMs = (ms: number | null | undefined): string => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
};

/**
 * Format an epoch ms timestamp to a readable date-time string.
 */
export const formatEpochMs = (epochMs: number): string => {
    return new Date(epochMs).toLocaleString();
};

/**
 * Format an ISO timestamp to a readable date-time string.
 */
export const formatIsoTimestamp = (iso: string | null): string => {
    if (iso == null) return 'Never';
    return new Date(iso).toLocaleString();
};

/**
 * Calculate time elapsed since a given ISO timestamp.
 * Returns a human-readable string like "2m ago", "3h ago", "1d ago".
 */
export const timeAgo = (iso: string | null): string => {
    if (iso == null) return 'Never';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'just now';
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};
